import {apiFetch} from '@/services/ApiClient.ts';
import {QrScannerService} from "@/services/QrScannerService.ts";
import {useAuth} from '@/composables/useAuth';
import {Result} from "@/utils/types.ts";
import {BARCODE_SCANNER} from "@/utils/constants.ts";

const {user} = useAuth();

interface PermissionDetails {
    action: string;
    payload: string;
    validSeconds: number;
    storeId: string;
    validUntil: string;
}

export class PermissionService {
    static async qrScanAction(hash: string, auth: boolean) {
        const employeeId = user.value?.Id;
        const url = auth ? '/auth/qrScanAction' : '/qrScanAction'
        const response = await apiFetch(url, {
            method: 'POST',
            body: JSON.stringify({'hash': hash, 'employeeId': employeeId})
        });

        const data = await response.json();

        if (!response.ok) {
            throw data;
        }

        return data.data?.data || data.data;
    };

    static async onboardingScanAction(rawUrl: string) {
        let baseUrl: string;
        let actionId: string | null;

        try {
            const urlObj = new URL(rawUrl);
            baseUrl = urlObj.origin;
            actionId = urlObj.searchParams.get('actionId');
        } catch (err) {
            throw new Error("Invalid onboarding QR format. Must be a valid initialization URL.");
        }

        if (!actionId) {
            throw new Error("Missing transaction payload key 'actionId' from onboarding configuration link.");
        }

        try {
            const response = await apiFetch('/qrScanAction', {
                method: 'POST',
                body: JSON.stringify({'hash': actionId}),
                baseUrlOverride: baseUrl
            });

            const data = await response.json();
            if (!response.ok) throw data;

            return data.data?.data || data.data;

        } catch (error) {
            throw error;
        }
    }

    static async scanOnBoardingAndProcess<T>(): Promise<Result<T>> {
        try {
            const rawUrl = await QrScannerService.scan();
            if (rawUrl === BARCODE_SCANNER.INTERNAL.CANCELLED) return { success: true, data: null as T }; // silent exit
            if (!rawUrl) return {
                success: false,
                error: {
                    msg: 'auth.errors.qrError'
                }
            };
            return {
                success: true,
                data: await PermissionService.onboardingScanAction(rawUrl)
            }
        } catch (error: any) {
            return {
                success: false,
                error: {
                    msg: 'auth.errors.qrError'
                }
            }
        }
    };

    static async scanAndProcess<T>(checkAuth: boolean): Promise<Result<T>> {
        try {
            const hash = await QrScannerService.scan();
            if (hash === BARCODE_SCANNER.INTERNAL.CANCELLED) return { success: true, data: null as T }; // silent exit
            if (!hash) return {
                success: false,
                error: {
                    msg: 'auth.errors.qrError'
                }
            };
            return {
                success: true,
                data: await PermissionService.qrScanAction(hash, checkAuth)
            }
        } catch (error: any) {
            return {
                success: false,
                error: {
                    msg: 'auth.errors.qrError'
                }
            }
        }
    };

    static async getPermissionDetails(hash: string): Promise<PermissionDetails> {
        const request = {employeeId: user.value?.Id, hash};

        const response = await apiFetch('getPermissionDetails', {
            method: 'POST',
            body: JSON.stringify(request),
        });
        const data = await response.json();
        if (!response.ok) {
            throw data;
        }

        return data.data.data as PermissionDetails;
    };

    static async permissionApproval(approvalId: string, isApproved: boolean = false) {
        const request = {'approvalId': approvalId, 'isApproved': isApproved};

        const response = await apiFetch('approveAction', {
            method: 'POST',
            body: JSON.stringify(request),
        });

        const data = await response.json();
        if (!response.ok) {
            throw data;
        }

        const result = data.data?.data || data.data;
        if (result?.accepted === false) {
            throw { error: result.error };
        }
        return result;
    };
}